import React from "react";
import { X } from "lucide-react";
import { EVENT_TYPE_LABELS, DAYS } from "./galleryConstants";
import { inputCls, labelCls } from "./adminUi";

const TOPIC_OPTIONS = ["Faith", "Family", "Prayer", "Prophecy", "Daniel & Revelation", "Youth", "Hope"];

const Field = ({ label, testId, ...props }) => (
  <div>
    <label className={labelCls}>{label}</label>
    <input data-testid={testId} className={inputCls} {...props} />
  </div>
);

const SermonFields = ({ data, set }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Speaker *" type="text" value={data.speaker} onChange={set("speaker")} required testId="modal-input-speaker" />
      <Field label="Date *" type="date" value={data.date} onChange={set("date")} required testId="modal-input-date" />
    </div>
    <div>
      <label className={labelCls}>Topic *</label>
      <select value={data.topic} onChange={set("topic")} required data-testid="modal-input-topic" className={inputCls}>{TOPIC_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
    </div>
    <Field label="YouTube URL" type="url" value={data.youtube_url} onChange={set("youtube_url")} placeholder="https://youtube.com/watch?v=..." testId="modal-input-youtube" />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Audio URL" type="url" value={data.audio_url} onChange={set("audio_url")} testId="modal-input-audio" />
      <Field label="Notes URL" type="url" value={data.notes_url} onChange={set("notes_url")} testId="modal-input-notes" />
    </div>
  </>
);

const EventFields = ({ data, set, setPublished }) => {
  const t = data.event_type;
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Event Type *</label>
          <select value={t} onChange={set("event_type")} data-testid="modal-input-event-type" className={inputCls}>
            {Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <Field label="Speaker" type="text" value={data.speaker} onChange={set("speaker")} testId="modal-input-speaker" />
      </div>
      {t === "weekly" && (
        <div>
          <label className={labelCls}>Repeats every *</label>
          <select value={data.recurrence_day || "Saturday"} onChange={set("recurrence_day")} data-testid="modal-input-recurrence-day" className={inputCls}>
            {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t === "weekly" ? "Start Date (optional)" : t === "date_range" ? "Start Date *" : "Date *"} type="date" value={data.date} onChange={set("date")} required={t !== "weekly"} testId="modal-input-date" />
        {t !== "one_time" && (
          <Field label={t === "weekly" ? "End Date (optional)" : "End Date *"} type="date" value={data.end_date} onChange={set("end_date")} required={t === "date_range"} testId="modal-input-end-date" />
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Start Time" type="text" value={data.start_time || ""} onChange={set("start_time")} placeholder="e.g. 7:00 PM Central Time" testId="modal-input-start-time" />
        <Field label="End Time" type="text" value={data.end_time || ""} onChange={set("end_time")} placeholder="e.g. 8:00 PM Central Time" testId="modal-input-end-time" />
      </div>
      <Field label="Location / Platform" type="text" value={data.location} onChange={set("location")} placeholder="e.g. Zoom, or 271 Walton St, Richardson, TX" testId="modal-input-location" />
      <label className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-sm cursor-pointer hover:border-[#003B5C]">
        <input type="checkbox" checked={!!data.published} onChange={(e) => setPublished(e.target.checked)} data-testid="modal-input-event-published" className="accent-[#003B5C]" />
        <span className="text-sm text-[#1E1E1E]">Published (uncheck to keep as a hidden draft until details are confirmed)</span>
      </label>
    </>
  );
};

/** Modal form for creating/editing a sermon or an event. */
export const ContentModal = ({ modal, setModal, onSubmit }) => {
  const set = (k) => (ev) => setModal({ ...modal, data: { ...modal.data, [k]: ev.target.value } });
  const setPublished = (v) => setModal({ ...modal, data: { ...modal.data, published: v } });

  return (
    <div data-testid="admin-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center">
          <h3 className="font-display text-xl font-bold text-[#003B5C]">{modal.id ? "Edit" : "New"} {modal.type === "sermon" ? "Sermon" : "Event"}</h3>
          <button onClick={() => setModal(null)} data-testid="admin-modal-close" className="p-2 hover:bg-gray-100 rounded-sm"><X size={16} /></button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <Field label="Title *" type="text" value={modal.data.title} onChange={set("title")} required testId="modal-input-title" />
          {modal.type === "sermon" ? <SermonFields data={modal.data} set={set} /> : <EventFields data={modal.data} set={set} setPublished={setPublished} />}
          <div>
            <label className={labelCls}>Description</label>
            <textarea rows={4} value={modal.data.description} onChange={set("description")} data-testid="modal-input-description" className={`${inputCls} resize-none`} />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button type="button" onClick={() => setModal(null)} className="px-5 py-2.5 text-sm font-medium text-[#1E1E1E]/70 hover:text-[#003B5C]">Cancel</button>
            <button type="submit" data-testid="modal-save-button" className="bg-[#003B5C] text-white px-6 py-2.5 rounded-sm text-sm font-medium hover:bg-[#002A42]">{modal.id ? "Update" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};
